'use client';

/**
 * Hero call arm (Wave A3 v2, split out of HeroSmartInput in Wave H1) —
 * "pick your flag, type your number, Tessa calls YOU".
 *
 * Behavior is the pre-H1 form, unchanged: the shell (HeroSmartInput.tsx)
 * decides WHETHER this arm renders (variant `call`: pct unset/0, `?cta=call`,
 * or no message number configured); this file owns everything that happens
 * once it does. Two additions only: the hero attribution ref travels in the
 * POST body (→ `onboarding_calls.campaign='hero:<ref>'`) and every action
 * reports through `onAction` (hero_cta_click).
 *
 * v2 (2026-08-17): the raw text field became a served-country phone input
 * (components/auth/PhoneInput — react-phone-number-input: flag select with
 * geo-detected default, national formatting, E.164 composition). Nobody
 * loses the call to a missing country code anymore — the founder himself
 * typed a bare Yukon number and would have gotten `country_not_served`.
 * SERVED_COUNTRIES mirrors SERVED_COUNTRY_PREFIXES
 * (lib/billing/dial-policy.ts) — keep the two in sync when a market opens;
 * the list is NOT imported to keep the client bundle free of billing code.
 *
 * The "prefer to message?" line (SMS/WhatsApp deep links) is rendered from
 * the hrefs the shell builds — platform-aware `sms:` separator (iOS `&body=`
 * vs Android `?body=`) and the ref'd body.
 *
 * The Turnstile widget is route-owned (the wizard's token is single-use and
 * Supabase-consumed) with the same remount-nonce idiom the wizard uses.
 */

import { useMemo, useState, type Ref } from 'react';
import Link from 'next/link';
import { Turnstile } from '@marsidev/react-turnstile';
import { Loader2, PhoneCall, Sparkles } from 'lucide-react';
import { isValidPhoneNumber, type Country } from 'react-phone-number-input';
import { cn } from '@/lib/utils';
import { PhoneInput } from '@/components/auth/PhoneInput';
import type { HeroCtaChannel } from '@/lib/onboarding/hero-cta';
import type { HeroCtaCopy } from '@/lib/onboarding/hero-cta-copy';
import { DEFAULT_SITE_LOCALE } from '@/lib/marketing/locale-routing';
import type { SiteLocale } from '@/lib/marketing/page-metadata';
import { OnboardingCallStatus } from './OnboardingCallStatus';
import { WhatsAppGlyph } from './WhatsAppGlyph';

type Mode = 'phone' | 'domain' | 'unknown';

// Mirror of SERVED_COUNTRY_PREFIXES (lib/billing/dial-policy.ts) — 28
// countries as of the 2026-08-19 expansion. Keep the two lists in sync when
// a market opens; the library renders flags/dial codes/labels itself.
const SERVED_COUNTRIES: Country[] = [
  'US', 'CA', 'MX', 'ES', 'FR', 'GB', 'BR', 'BO',
  'IT', 'DE', 'SE', 'NO', 'CH', 'FI', 'NL', 'BE', 'PT',
  'JP', 'CN', 'IN', 'KR', 'AU', 'NZ',
  'CO',
  'ZA', 'MA', 'NG', 'EG',
];

export function detectMode(raw: string): Mode {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/[\s\-().+]/g, '');
  if (digits.length >= 7 && /^\d+$/.test(digits)) return 'phone';
  // Domain-ish (Wave B): letters + at least one dot, no spaces/@ — "acme.com",
  // "https://acme.com/about" both count; the route re-normalizes strictly.
  if (
    !trimmed.includes(' ') &&
    !trimmed.includes('@') &&
    /^[a-z0-9.:/\-_]+$/i.test(trimmed) &&
    /[a-z]/i.test(trimmed) &&
    trimmed.replace(/^https?:\/\//i, '').includes('.')
  ) {
    return 'domain';
  }
  return 'unknown';
}

type Phase =
  | { name: 'idle' }
  | { name: 'submitting' }
  | { name: 'live'; callId: string; token: string }
  | { name: 'account_exists' }
  | { name: 'error'; message: string };

// Pure predicates for the call CTA, exported for unit tests (the vitest
// harness is node-env with *.test.ts globs only — same idiom as
// components/routines/__tests__ — so the mounted component is covered by
// typecheck + e2e, and the recoverability logic is covered here).
export type PhaseName = Phase['name'];

export function shouldShowCaptcha(args: {
  enabled: boolean;
  hasCaptchaSiteKey: boolean;
  entryMode: 'phone' | 'domain';
  phoneReady: boolean;
  phaseName: PhaseName;
}): boolean {
  // 'error' is included so a failed submit recovers in place: the consumed
  // token was nulled and captchaNonce bumped, so the widget remounts and
  // mints a fresh token — otherwise the CTA dead-ends while copy.errors says
  // "try again". Hidden during 'submitting'/'live'/'account_exists' as before.
  return (
    args.enabled &&
    args.hasCaptchaSiteKey &&
    args.entryMode === 'phone' &&
    args.phoneReady &&
    (args.phaseName === 'idle' || args.phaseName === 'error')
  );
}

export function isCallSubmitDisabled(args: {
  phaseName: PhaseName;
  phoneReady: boolean;
  hasCaptchaSiteKey: boolean;
  captchaToken: string | null;
}): boolean {
  return (
    args.phaseName === 'submitting' ||
    !args.phoneReady ||
    (args.hasCaptchaSiteKey && !args.captchaToken)
  );
}

export interface HeroCallFormProps {
  className?: string;
  /** The shell's IntersectionObserver target (hero_cta_view at ≥50%). */
  rootRef?: Ref<HTMLDivElement>;
  /** Platform-aware `sms:` deep link with the ref'd body; '' when no SMS number. */
  smsHref: string;
  /** `wa.me` link with the ref'd body; '' when no WhatsApp number. */
  waHref: string;
  /** The SMS number formatted for display. */
  smsDisplay: string;
  /** Hero attribution ref — travels in the call POST (campaign stamp). */
  heroRef: string | null;
  onAction: (channel: HeroCtaChannel) => void;
  copy: HeroCtaCopy;
  /** The page's language. A translated page sends it with the call so Tessa
   *  opens in it; an English page leaves the phone country to decide. */
  locale: SiteLocale;
}

export function HeroCallForm({
  className,
  rootRef,
  smsHref,
  waHref,
  smsDisplay,
  heroRef,
  onAction,
  copy,
  locale,
}: HeroCallFormProps) {
  const enabled =
    (process.env.NEXT_PUBLIC_ONBOARDING_CALL_ENABLED ?? '').toLowerCase() === 'true';
  const captchaSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  const [phoneValue, setPhoneValue] = useState('');
  const [domainValue, setDomainValue] = useState('');
  const [entryMode, setEntryMode] = useState<'phone' | 'domain'>('phone');
  const [phase, setPhase] = useState<Phase>({ name: 'idle' });
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaNonce, setCaptchaNonce] = useState(0);

  const phoneReady = useMemo(
    () => !!phoneValue && isValidPhoneNumber(phoneValue),
    [phoneValue]
  );
  // Show the captcha only once the number is complete — zero widget noise
  // for visitors who never engage. Stays mounted through phase 'error' so a
  // failed submit can mint a fresh token (see shouldShowCaptcha).
  const showCaptcha = shouldShowCaptcha({
    enabled,
    hasCaptchaSiteKey: !!captchaSiteKey,
    entryMode,
    phoneReady,
    phaseName: phase.name,
  });

  if (!enabled) return null;
  if (phase.name === 'live') {
    return <OnboardingCallStatus callId={phase.callId} token={phase.token} copy={copy.status} />;
  }

  // The domain draft lands in the English wizard, so only English pages offer it.
  const domainDraftEnabled =
    (process.env.NEXT_PUBLIC_ONBOARDING_DOMAIN_DRAFT ?? '').toLowerCase() === 'true' &&
    locale === DEFAULT_SITE_LOCALE;

  const clearTransientPhase = () => {
    if (phase.name === 'error' || phase.name === 'account_exists') {
      setPhase({ name: 'idle' });
    }
  };

  const submit = async () => {
    if (phase.name === 'submitting') return;

    // Wave B: domain mode — draft from the company's own web presence, land
    // in the prefilled wizard. Fail-open: any non-prefilled answer just goes
    // to the plain wizard (the funnel that already works).
    if (entryMode === 'domain' && domainDraftEnabled) {
      if (detectMode(domainValue) !== 'domain') return;
      setPhase({ name: 'submitting' });
      try {
        const res = await fetch('/api/public/draft-from-domain', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ domain: domainValue.trim() }),
        });
        const data = await res.json().catch(() => null);
        window.location.href =
          res.ok && data?.prefilled && data?.redirectTo
            ? data.redirectTo
            : '/routines/new?campaign=domain-draft';
        return;
      } catch {
        window.location.href = '/routines/new?campaign=domain-draft';
        return;
      }
    }

    if (!phoneReady) return;
    onAction('call');
    setPhase({ name: 'submitting' });
    try {
      const res = await fetch('/api/public/onboarding-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phoneValue,
          turnstileToken: captchaToken ?? '',
          ...(heroRef ? { ref: heroRef } : {}),
          ...(locale !== DEFAULT_SITE_LOCALE ? { lang: locale } : {}),
        }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.status === 'dialing') {
        setPhase({ name: 'live', callId: data.id, token: data.token });
        return;
      }
      if (res.ok && data?.status === 'account_exists') {
        // The route consumed this token too (verifyTurnstile is single-use):
        // clear + remint like the error paths, or the next submit after a
        // phone edit fires a guaranteed captcha_failed round-trip.
        setCaptchaNonce((n) => n + 1);
        setCaptchaToken(null);
        setPhase({ name: 'account_exists' });
        return;
      }
      setCaptchaNonce((n) => n + 1); // consumed token can't be reused
      setCaptchaToken(null);
      setPhase({
        name: 'error',
        message: copy.errors[data?.error as keyof HeroCtaCopy['errors']] ?? copy.errors.call_unavailable,
      });
    } catch {
      setCaptchaNonce((n) => n + 1);
      setCaptchaToken(null);
      setPhase({ name: 'error', message: copy.networkError });
    }
  };

  return (
    <div ref={rootRef} className={cn('mb-5 flex w-full max-w-[480px] flex-col gap-2', className)}>
      {entryMode === 'phone' ? (
        <div className="flex flex-col gap-2 sm:flex-row">
          <PhoneInput
            value={phoneValue}
            onChange={(v) => {
              setPhoneValue(v ?? '');
              clearTransientPhase();
            }}
            countries={SERVED_COUNTRIES}
            international={false}
            placeholder={copy.phonePlaceholder}
            className="flex h-12 min-w-0 flex-1 items-center rounded-full border border-border bg-background pl-4 pr-2 transition-colors focus-within:border-[#EF4444]"
            numberInputClassName="notranslate h-full w-full min-w-0 border-0 bg-transparent px-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:outline-none"
            countrySelectClassName="bg-transparent text-[15px] focus:outline-none"
          />
          <button
            onClick={submit}
            disabled={isCallSubmitDisabled({
              phaseName: phase.name,
              phoneReady,
              hasCaptchaSiteKey: !!captchaSiteKey,
              captchaToken,
            })}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#EF4444] px-6 text-[15px] font-semibold text-white transition-all hover:bg-[#DC2626] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {phase.name === 'submitting' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PhoneCall className="h-4 w-4" />
            )}
            {copy.callMe}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            placeholder={copy.domainPlaceholder}
            value={domainValue}
            onChange={(e) => {
              setDomainValue(e.target.value);
              clearTransientPhase();
            }}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            spellCheck={false}
            translate="no"
            className="notranslate h-12 flex-1 rounded-full border border-border bg-background px-5 text-[15px] outline-none focus:border-[#EF4444]"
          />
          <button
            onClick={submit}
            disabled={phase.name === 'submitting' || detectMode(domainValue) !== 'domain'}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#EF4444] px-6 text-[15px] font-semibold text-white transition-all hover:bg-[#DC2626] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {phase.name === 'submitting' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            {copy.draftMyCheckIn}
          </button>
        </div>
      )}

      {showCaptcha && (
        <Turnstile
          key={captchaNonce}
          siteKey={captchaSiteKey!}
          onSuccess={setCaptchaToken}
          onExpire={() => setCaptchaToken(null)}
          onError={() => setCaptchaToken(null)}
          options={{ theme: 'auto', size: 'flexible' }}
        />
      )}

      {(smsHref || waHref) && entryMode === 'phone' && phase.name !== 'account_exists' && (
        <p className="hsi-muted text-[13px] text-muted-foreground">
          {/* WhatsApp leads — the deskless/LATAM ICP lives there, and wa.me
              also works on desktop (WhatsApp Web) where sms: links do not. */}
          {waHref && smsHref ? (
            <>
              {copy.preferMessage}{' '}
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onAction('whatsapp')}
                className="hsi-strong whitespace-nowrap font-medium text-foreground underline-offset-4 hover:underline"
              >
                <WhatsAppGlyph />
                WhatsApp
              </a>{' '}
              {copy.orText}{' '}
              <a
                href={smsHref}
                onClick={() => onAction('sms')}
                className="hsi-strong whitespace-nowrap font-medium text-foreground underline-offset-4 hover:underline"
              >
                {smsDisplay}
              </a>
            </>
          ) : waHref ? (
            <>
              {copy.preferMessage}{' '}
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onAction('whatsapp')}
                className="hsi-strong whitespace-nowrap font-medium text-foreground underline-offset-4 hover:underline"
              >
                <WhatsAppGlyph />
                WhatsApp
              </a>
            </>
          ) : (
            <>
              {copy.preferText}{' '}
              <a
                href={smsHref}
                onClick={() => onAction('sms')}
                className="hsi-strong whitespace-nowrap font-medium text-foreground underline-offset-4 hover:underline"
              >
                {smsDisplay}
              </a>
            </>
          )}
        </p>
      )}

      {domainDraftEnabled && phase.name !== 'submitting' && (
        <button
          type="button"
          onClick={() => {
            setEntryMode(entryMode === 'phone' ? 'domain' : 'phone');
            clearTransientPhase();
          }}
          className="hsi-muted w-fit text-[13px] text-muted-foreground underline-offset-4 hover:underline"
        >
          {entryMode === 'phone'
            ? copy.domainToggle
            : copy.backToCall}
        </button>
      )}

      {phase.name === 'error' && (
        <p className="text-[13px] text-red-600 dark:text-red-400">
          {phase.message}{' '}
          {/* The wizard exit lives HERE, not under the idle hero — several
              copy.errors lines promise "build your check-in below", so the
              escape hatch appears exactly when the call path failed. */}
          <Link
            href="/routines/new"
            className="hsi-muted whitespace-nowrap text-muted-foreground underline underline-offset-4"
          >
            {copy.startFromTemplate}
          </Link>
        </p>
      )}
      {phase.name === 'account_exists' && (
        <p className="hsi-muted text-[13px] text-muted-foreground">
          {copy.accountExists.before}{' '}
          <Link href="/login" className="underline">
            {copy.accountExists.link}
          </Link>{' '}
          {copy.accountExists.after}
        </p>
      )}
    </div>
  );
}
